import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {Button, Card, Descriptions, Space, Tabs, Typography, Spin} from "antd";
import {ArrowLeftOutlined, EditOutlined} from "@ant-design/icons";
import {getGroupById, clearCurrentGroup} from "../../../../../store/slices/MicroqualificationGroupsSlice";
import {LocalName} from "../../../../../utils/LocalName";
import {format} from "date-fns";
import StudentsTab from "./tabs/StudentsTab";
import GradesTab from "./tabs/GradesTab";
import DocumentsTab from "./tabs/DocumentsTab";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";

const {Title, Text} = Typography;

const GroupDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {groupId} = useParams();
    const [activeTab, setActiveTab] = useState('info');

    const {currentGroup, groupsLoading} = useSelector(state => state.microqualificationGroups);

    useEffect(() => {
        if (groupId) {
            dispatch(getGroupById(groupId));
        }
        return () => {
            dispatch(clearCurrentGroup());
        };
    }, [groupId, dispatch]);

    if (groupsLoading && !currentGroup) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (!currentGroup) {
        return (
            <div style={{width: '100%', height: '100%'}}>
                <Card>
                    <Text><IntlMessage id="mqGroups.groupNotFound"/></Text>
                </Card>
            </div>
        );
    }

    const tabItems = [
        {
            key: 'info',
            label: <IntlMessage id="information"/>,
            children: (
                <Card>
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.groupName"/>} span={2}>
                            <Text strong style={{fontSize: 16}}>{currentGroup.name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.academicYear"/>}>
                            {currentGroup.academicYear}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.studentCount"/>}>
                            {currentGroup.studentCount || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.startDate"/>}>
                            {format(new Date(currentGroup.startDate), 'dd.MM.yyyy')}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.endDate"/>}>
                            {format(new Date(currentGroup.endDate), 'dd.MM.yyyy')}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.microqualificationName"/>} span={2}>
                            {LocalName.getName(currentGroup)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.credits"/>}>
                            {currentGroup.credits || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.disciplinesCount"/>}>
                            {currentGroup.disciplines?.length || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id="mqGroups.teacher"/>} span={2}>
                            {currentGroup.teacherName}
                            <br/>
                            <Text type="secondary" style={{fontSize: 12}}>
                                {currentGroup.teacher?.email}
                            </Text>
                        </Descriptions.Item>
                    </Descriptions>

                    {currentGroup.microqualification?.disciplines?.length > 0 && (
                        <div style={{marginTop: 24}}>
                            <Title level={5}><IntlMessage id="mqGroups.disciplines"/></Title>
                            <Descriptions bordered column={1}>
                                {currentGroup.microqualification.disciplines.map((discipline, index) => (
                                    <Descriptions.Item
                                        key={discipline.id}
                                        label={`${index + 1}. ${discipline.code}`}
                                    >
                                        <div>
                                            <Text>{LocalName.getName(discipline)}</Text>
                                            <br/>
                                            <Text type="secondary" style={{fontSize: 12}}>
                                                <IntlMessage id="mqGroups.credits"/>: {discipline.creditNumbers}
                                            </Text>
                                        </div>
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        </div>
                    )}
                </Card>
            )
        },
        {
            key: 'students',
            label: <><IntlMessage id="mqGroups.students"/> ({currentGroup.studentCount || 0})</>,
            children: <StudentsTab groupId={groupId}/>
        },
        {
            key: 'grades',
            label: <IntlMessage id="tg.grades"/>,
            children: <GradesTab groupId={groupId} group={currentGroup}/>
        },
        {
            key: 'documents',
            label: <IntlMessage id="mqGroups.documentsTab"/>,
            children: <DocumentsTab groupId={groupId} group={currentGroup}/>
        }
    ];

    return (
        <div style={{width: '100%', height: '100%'}}>
            <Card style={{marginBottom: 16}} bordered={false}>
                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                    <Space>
                        <Button
                            icon={<ArrowLeftOutlined/>}
                            onClick={() => navigate('/p/microqualification-groups')}
                        >
                            <IntlMessage id="back"/>
                        </Button>
                        <Title level={3} style={{margin: 0}}>
                            {currentGroup.name}
                        </Title>
                    </Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined/>}
                        onClick={() => navigate(`/p/microqualification-groups/${groupId}/edit`)}
                    >
                        <IntlMessage id="mqGroups.editGroup"/>
                    </Button>
                </Space>
            </Card>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
            />
        </div>
    );
};

export default GroupDetails;
